import { useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { useParams } from 'react-router-dom';
import {
  CurrentScheduleItem,
  colors,
  DataTable,
  formatDateTimeShortWithLocale,
} from 'remote/global';
import { useTranslation } from 'react-i18next';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import { Paper, Grid, Button } from '@mui/material';
import { useOperatorsScheduleVehiclesRepairLogQuery } from 'api/queries/operators';
import { PaginationQueryParams } from 'api/types';
import { formatDateWithTz, hasWritePermission } from 'utils/helpers';
import RepairModal from 'components/modals/VehicleRepairModal';

function VehicleRepairLogTable() {
  const [activeElement, setActiveElement] = useState<CurrentScheduleItem | null>(null);
  const [isRepairModalOpen, setIsRepairModalOpen] = useState(false);
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { id } = useParams();
  const [queryParams, setQueryParams] = useState<PaginationQueryParams>({
    pageNo: '0',
  });
  const {
    data: log,
    isLoading: isRepairLogLoading,
    isSuccess: isRepairLogSuccess,
  } = useOperatorsScheduleVehiclesRepairLogQuery(id!, queryParams);
  const { t, i18n } = useTranslation();
  const { permissions = {} } = userInfo ?? {};

  const handleClearRepairModalState = () => {
    setIsRepairModalOpen(false);
    setActiveElement(null);
  };

  const commonColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'startTime',
        headerName: t('FleetView.DetailView.vehicleRepairLog.table.header.startTime').toString(),
        flex: 1,
        valueGetter: (params) => {
          return formatDateTimeShortWithLocale(params.row.startTime, i18n.language);
        },
      },
      {
        field: 'endTime',
        headerName: t('FleetView.DetailView.vehicleRepairLog.table.header.endTime').toString(),
        flex: 1,
        valueGetter: (params) => {
          return formatDateTimeShortWithLocale(params.row.endTime, i18n.language);
        },
      },
      {
        field: 'replacementBus',
        headerName: t(
          'FleetView.DetailView.vehicleRepairLog.table.header.replacementBus'
        ).toString(),
        flex: 1,
      },
    ],
    [t, i18n.language]
  );

  const columnsWithWritePermissions: GridColDef[] = useMemo(
    () => [
      ...commonColumns,
      {
        field: ' ',
        align: 'right',
        headerName: '',
        flex: 1,
        renderCell: (params) => {
          return (
            <Button
              variant='text'
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                '&.MuiButton-text': { color: colors.uiBlue600 },
              }}
              onClick={() => {
                setActiveElement(params.row);
                setIsRepairModalOpen(true);
              }}
            >
              {t('edit')}
            </Button>
          );
        },
      },
    ],
    [commonColumns, t]
  );

  if (!isRepairLogSuccess) return null;

  return (
    <>
      <RepairModal
        open={isRepairModalOpen}
        setOpen={setIsRepairModalOpen}
        onClose={handleClearRepairModalState}
        isEditMode
        defaultValues={{
          startTime: formatDateWithTz(activeElement?.startTime),
          endTime: formatDateWithTz(activeElement?.endTime),
          ownerId: Number(activeElement?.ownerId) ?? '',
          resourceId: Number(activeElement?.id),
        }}
      />
      <Grid item xs={12} textAlign='left'>
        <Paper
          sx={{
            height: 400,
            width: '100%',
            background: 'transparent',
            '& .MuiDataGrid-columnHeader': {
              background: `${colors.primaryBlue5} !important`,
            },
            '.MuiDataGrid-row:nth-of-type(odd) .MuiDataGrid-cell': {
              background: 'transparent !important',
            },
          }}
        >
          <DataTable
            rows={log ?? []}
            columns={
              hasWritePermission(permissions?.VEHICLE_SCHEDULE)
                ? columnsWithWritePermissions
                : commonColumns
            }
            loading={isRepairLogLoading}
            getRowId={(row) => row.id}
            onPageChange={(page) => setQueryParams((p) => ({ ...p, pageNo: String(page) }))}
            t={t}
          />
        </Paper>
      </Grid>
    </>
  );
}

export default VehicleRepairLogTable;
